import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowRight, ArrowLeft, Calculator, Coins, Sparkles, Trees, TrendingUp,
    Building, Banknote, Handshake, ShieldCheck,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const PROJECT_TYPES = [
    { id: "residential", label: "Residential", multiplier: 1.5 },
    { id: "commercial", label: "Commercial", multiplier: 2.0 },
    { id: "tourism", label: "Tourism / Resort", multiplier: 2.5 },
    { id: "mixed_use", label: "Mixed-use", multiplier: 2.0 },
    { id: "subdivision", label: "Subdivision", multiplier: 1.0 },
];

const fmtFjd = (n) =>
    new Intl.NumberFormat("en-FJ", { style: "currency", currency: "FJD", maximumFractionDigits: 0 }).format(n || 0);

export default function JVCalculator() {
    const [params] = useSearchParams();
    const [askingPrice, setAskingPrice] = useState(Number(params.get("price") || 500000));
    const [projectType, setProjectType] = useState(params.get("type") || "tourism");
    const [equityPct, setEquityPct] = useState([25]);
    const [horizon, setHorizon] = useState([10]);
    const [roiPct, setRoiPct] = useState([19]);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const compute = async () => {
        setLoading(true);
        try {
            const projType = PROJECT_TYPES.find((p) => p.id === projectType);
            const data = await api.post("/agent/jv-calculator", {
                asking_price_fjd: Number(askingPrice),
                project_type: projectType,
                landowner_equity_pct: equityPct[0],
                horizon_years: horizon[0],
                expected_annual_roi_pct: roiPct[0],
                capital_multiplier: projType?.multiplier ?? 2.0,
            });
            setResult(data.data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not compute");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        compute();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div data-testid="jv-calculator-page" className="bg-background min-h-screen">
            {/* Hero */}
            <section className="relative overflow-hidden bg-ocean-deep-grad text-white py-16 lg:py-20">
                <div className="absolute -top-40 -right-40 h-[400px] w-[400px] rounded-full bg-coral/10 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-40 -left-40 h-[400px] w-[400px] rounded-full bg-fiji-green/10 blur-3xl pointer-events-none" />
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/15 border border-coral/30 mb-5">
                        <Calculator className="h-3.5 w-3.5 text-coral" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                            For agents · Show your landowners
                        </span>
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-4">
                        Sale vs <span className="italic text-turquoise">Joint Venture</span>.
                    </h1>
                    <p className="font-app text-base sm:text-lg text-white/85 max-w-2xl leading-relaxed">
                        Pull this up on your phone in front of any landowner. Plug in the asking price, walk through the numbers,
                        and show them what they're leaving on the table by selling outright.
                    </p>
                </div>
            </section>

            <section className="py-12 lg:py-16">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-8">
                    {/* Inputs */}
                    <Card className="p-6 lg:p-7 bg-card lg:col-span-2 h-fit lg:sticky lg:top-24">
                        <div className="flex items-center gap-2 mb-5">
                            <div className="h-9 w-9 rounded-md bg-coral/10 grid place-items-center">
                                <Coins className="h-4 w-4 text-coral" />
                            </div>
                            <h2 className="font-display text-lg font-bold text-foreground">Inputs</h2>
                        </div>

                        <div className="space-y-5">
                            <div>
                                <Label className="text-xs uppercase tracking-wider font-app text-muted-foreground mb-1.5 block">
                                    Asking price (FJD)
                                </Label>
                                <Input
                                    type="number"
                                    value={askingPrice}
                                    onChange={(e) => setAskingPrice(Number(e.target.value))}
                                    min={1000}
                                    step={1000}
                                    data-testid="jv-calc-price-input"
                                    className="bg-background text-foreground font-app text-lg font-semibold"
                                />
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-wider font-app text-muted-foreground mb-1.5 block">
                                    Project type
                                </Label>
                                <Select value={projectType} onValueChange={setProjectType}>
                                    <SelectTrigger data-testid="jv-calc-type-select" className="bg-background text-foreground font-app">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PROJECT_TYPES.map((p) => (
                                            <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-wider font-app text-muted-foreground mb-1.5 block">
                                    Landowner equity:&nbsp;
                                    <span className="font-bold text-foreground tabular-nums">{equityPct[0]}%</span>
                                </Label>
                                <Slider value={equityPct} onValueChange={setEquityPct} min={5} max={50} step={1} data-testid="jv-calc-equity-slider" />
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-wider font-app text-muted-foreground mb-1.5 block">
                                    Horizon:&nbsp;
                                    <span className="font-bold text-foreground tabular-nums">{horizon[0]} years</span>
                                </Label>
                                <Slider value={horizon} onValueChange={setHorizon} min={1} max={20} step={1} data-testid="jv-calc-horizon-slider" />
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-wider font-app text-muted-foreground mb-1.5 block">
                                    Annual project ROI:&nbsp;
                                    <span className="font-bold text-foreground tabular-nums">{roiPct[0]}%</span>
                                </Label>
                                <Slider value={roiPct} onValueChange={setRoiPct} min={5} max={40} step={1} data-testid="jv-calc-roi-slider" />
                            </div>

                            <Button
                                onClick={compute}
                                disabled={loading}
                                data-testid="jv-calc-compute-btn"
                                className="w-full bg-coral hover:bg-coral-deep text-white font-app"
                            >
                                <TrendingUp className="h-4 w-4 mr-2" />
                                {loading ? "Computing…" : "Recalculate"}
                            </Button>
                        </div>
                    </Card>

                    {/* Results */}
                    <div className="lg:col-span-3 space-y-5">
                        {result && (
                            <>
                                {/* Headline comparison */}
                                <Card className="p-6 lg:p-8 bg-gradient-to-br from-fiji-green/5 to-coral/5 dark:from-fiji-green/10 dark:to-coral/10 border-fiji-green/30" data-testid="jv-calc-headline">
                                    <div className="text-[10px] uppercase tracking-[0.3em] font-app font-bold text-coral mb-2">
                                        The bottom line
                                    </div>
                                    {result.comparison.favored === "jv" ? (
                                        <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground leading-tight mb-3">
                                            Joint Venture beats outright sale by{" "}
                                            <span className="text-fiji-green tabular-nums">+{result.comparison.uplift_pct}%</span>{" "}
                                            over {result.inputs.horizon_years} years.
                                        </h3>
                                    ) : (
                                        <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground leading-tight mb-3">
                                            Outright sale wins for this scenario by{" "}
                                            <span className="text-coral tabular-nums">{Math.abs(result.comparison.uplift_pct)}%</span>.
                                        </h3>
                                    )}
                                    <p className="font-app text-sm text-muted-foreground leading-relaxed">
                                        Difference: <strong className="text-foreground">{fmtFjd(Math.abs(result.comparison.uplift_fjd))}</strong> {result.comparison.favored === "jv" ? "more" : "less"} for the landowner over the horizon.
                                    </p>
                                </Card>

                                {/* Side-by-side cards */}
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <Card className="p-5 bg-card border-border" data-testid="jv-calc-sale-card">
                                        <div className="flex items-center gap-2 mb-4">
                                            <Banknote className="h-4 w-4 text-muted-foreground" />
                                            <div className="font-display text-sm font-bold text-foreground">Outright sale</div>
                                        </div>
                                        <div className="space-y-2 font-app text-sm">
                                            <Row label="Gross asking" value={fmtFjd(result.sale.gross_fjd)} />
                                            <Row label="Net to landowner" value={fmtFjd(result.sale.net_fjd)} bold />
                                            <Row label="Assumed net %" value={`${result.sale.assumed_net_pct}%`} muted />
                                        </div>
                                        <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground font-app">
                                            One-time payment, then nothing. Done.
                                        </div>
                                    </Card>

                                    <Card className="p-5 bg-fiji-green/5 dark:bg-fiji-green/10 border-fiji-green/30" data-testid="jv-calc-jv-card">
                                        <div className="flex items-center gap-2 mb-4">
                                            <Handshake className="h-4 w-4 text-fiji-green" />
                                            <div className="font-display text-sm font-bold text-foreground">Joint Venture</div>
                                        </div>
                                        <div className="space-y-2 font-app text-sm">
                                            <Row label="Total project value" value={fmtFjd(result.jv.total_project_value_fjd)} />
                                            <Row label="Annual to landowner" value={fmtFjd(result.jv.annual_landowner_distribution_fjd)} />
                                            <Row label={`Cumulative @${result.jv.horizon_years}y`} value={fmtFjd(result.jv.total_distributions_fjd)} />
                                            <Row label="Retained equity" value={fmtFjd(result.jv.retained_equity_fjd)} />
                                            <Row label="Total landowner value" value={fmtFjd(result.jv.total_value_fjd)} bold accent />
                                        </div>
                                    </Card>
                                </div>

                                {/* Year-by-year table */}
                                <Card className="overflow-hidden bg-card" data-testid="jv-calc-year-table">
                                    <div className="grid grid-cols-4 bg-ocean-deep-grad text-white">
                                        {["Year", "Annual", "Cumulative", "Total value"].map((h) => (
                                            <div key={h} className="p-3 font-app text-[10px] uppercase tracking-wider font-bold">
                                                {h}
                                            </div>
                                        ))}
                                    </div>
                                    {result.jv.years.map((y, i) => (
                                        <div
                                            key={y.year}
                                            className={`grid grid-cols-4 ${i % 2 === 0 ? "bg-card" : "bg-secondary/40 dark:bg-card/60"} border-b border-border last:border-0`}
                                        >
                                            <div className="p-3 font-app text-sm font-semibold text-foreground tabular-nums">Year {y.year}</div>
                                            <div className="p-3 font-app text-sm text-foreground/85 tabular-nums">{fmtFjd(y.annual_distribution_fjd)}</div>
                                            <div className="p-3 font-app text-sm text-foreground/85 tabular-nums">{fmtFjd(y.cumulative_distributions_fjd)}</div>
                                            <div className="p-3 font-app text-sm font-semibold text-fiji-green tabular-nums">{fmtFjd(y.total_value_fjd)}</div>
                                        </div>
                                    ))}
                                </Card>

                                {/* CTA strip */}
                                <Card className="p-5 lg:p-6 bg-coral/5 dark:bg-coral/10 border-coral/30">
                                    <div className="flex items-start gap-4">
                                        <div className="h-10 w-10 rounded-md bg-coral/15 grid place-items-center shrink-0">
                                            <Sparkles className="h-5 w-5 text-coral" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-display text-base font-bold text-foreground mb-1">Ready to convert this listing?</div>
                                            <p className="font-app text-sm text-muted-foreground mb-3">
                                                If your landowner is in, take their listing into the JV pipeline in one click. We handle the rest.
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                <Button asChild data-testid="jv-calc-go-console-btn" className="bg-coral hover:bg-coral-deep text-white font-app">
                                                    <Link to="/agent-console">
                                                        Open Agent Console <ArrowRight className="h-3.5 w-3.5 ml-1" />
                                                    </Link>
                                                </Button>
                                                <Button asChild variant="outline" data-testid="jv-calc-go-explain-btn" className="font-app">
                                                    <Link to="/agents/explain-jv">
                                                        Explain JV in 60 seconds
                                                    </Link>
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </>
                        )}
                        {!result && !loading && (
                            <Card className="p-8 bg-card text-center">
                                <p className="font-app text-sm text-muted-foreground">Press recalculate to see numbers.</p>
                            </Card>
                        )}
                    </div>
                </div>
            </section>

            {/* Disclaimer footer */}
            <section className="py-8 bg-secondary/40 dark:bg-card/30 border-t border-border">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <p className="font-app text-xs text-muted-foreground leading-relaxed">
                        <ShieldCheck className="h-3 w-3 inline mr-1.5 text-fiji-green" />
                        Estimates only — actual returns depend on project execution, occupancy/rental yields, exit multiple, and market conditions.
                        Bula Real Estate Fiji acts as the platform intermediary; no part of this page constitutes financial advice.
                    </p>
                    <Button asChild variant="ghost" data-testid="jv-calc-back-btn" className="mt-3 font-app">
                        <Link to="/agents/explain-jv">
                            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back to JV explainer
                        </Link>
                    </Button>
                </div>
            </section>
        </div>
    );
}

function Row({ label, value, bold, muted, accent }) {
    return (
        <div className="flex items-baseline justify-between gap-3">
            <span className={`font-app ${muted ? "text-xs text-muted-foreground" : "text-xs text-muted-foreground"}`}>{label}</span>
            <span className={`tabular-nums ${bold ? "text-base font-bold" : "text-sm"} ${accent ? "text-fiji-green" : "text-foreground"}`}>
                {value}
            </span>
        </div>
    );
}
